import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { AutomationDto } from "@/shared/dtos/automation-dto";
import { AutomationStatus } from "@/shared/enums/automation-status";
import { ProtocolType } from "@/shared/enums/protocol-type";
import { useMemo, useState } from "react";

const SOURCE_TYPE_QUALITY_RULE = "QUALITY_RULE";
const SOURCE_TYPE_PROFILING = "PROFILING";

const STREAMING_PROTOCOLS = new Set<string>([
	ProtocolType.KAFKA,
	ProtocolType.MQTT,
	ProtocolType.OPCUA,
]);

const ERROR_STATUSES = [
	AutomationStatus.ERROR_NOT_ENOUGH_QUOTE,
	AutomationStatus.ERROR_PAUSED_EXHAUSTED_QUOTA,
	AutomationStatus.ERROR_CONFIGURING_POOL_PROCESSES,
	AutomationStatus.FAILED_TO_START,
	AutomationStatus.GENERIC_ERROR,
];

type SourceTypeInfo = { icon: string; labelKey: string };

const getSourceTypeInfo = (sourceType: string | undefined): SourceTypeInfo => {
	if (!sourceType)
		return {
			icon: "mdi-help-circle-outline",
			labelKey: "automation.type.unknown",
		};
	if (STREAMING_PROTOCOLS.has(sourceType))
		return { icon: "mdi-broadcast", labelKey: "automation.type.streaming" };
	if (sourceType === SOURCE_TYPE_QUALITY_RULE)
		return {
			icon: "mdi-shield-check-outline",
			labelKey: "automation.type.qualityRule",
		};
	if (sourceType === SOURCE_TYPE_PROFILING)
		return { icon: "mdi-chart-line", labelKey: "automation.type.profiling" };
	return {
		icon: "mdi-database-sync-outline",
		labelKey: "automation.type.database",
	};
};

const getStatusDotClass = (statusCode: string | undefined): string => {
	switch (statusCode) {
		case AutomationStatus.PENDING:
			return "pending";
		case AutomationStatus.STARTED:
			return "started";
		case AutomationStatus.PAUSED:
			return "paused";
		case AutomationStatus.FINISHED:
			return "finished";
		case AutomationStatus.DELETED:
			return "deleted";
		case AutomationStatus.ERROR_NOT_ENOUGH_QUOTE:
		case AutomationStatus.ERROR_PAUSED_EXHAUSTED_QUOTA:
		case AutomationStatus.ERROR_CONFIGURING_POOL_PROCESSES:
		case AutomationStatus.FAILED_TO_START:
			return "error";
		case AutomationStatus.GENERIC_ERROR:
			return "generic-error";
		default:
			return "unknown";
	}
};

export const useAutomationCard = (
	automation: AutomationDto,
	onPlay: () => Promise<void>,
	onPause: () => Promise<void>,
	onStop: () => Promise<void>,
	onResume: () => Promise<void>,
) => {
	const { t } = useLanguage();
	const [processingAction, setProcessingAction] = useState<string | null>(null);

	const statusCode = automation.status?.code;
	const { icon: typeIcon, labelKey: typeLabelKey } = getSourceTypeInfo(
		automation.sourceConfiguration?.sourceType,
	);
	const statusDotClass = getStatusDotClass(statusCode);
	const isPulsing = statusCode === AutomationStatus.STARTED;

	const translatedStatus = useMemo(() => {
		switch (statusCode) {
			case AutomationStatus.PENDING:
				return t("automation.status.pending");
			case AutomationStatus.STARTED:
				return t("automation.status.started");
			case AutomationStatus.PAUSED:
				return t("automation.status.paused");
			case AutomationStatus.FINISHED:
				return t("automation.status.finished");
			case AutomationStatus.DELETED:
				return t("automation.status.deleted");
			default:
				return t("automation.status.error");
		}
	}, [statusCode, t]);

	const isError = useMemo(
		() => ERROR_STATUSES.includes(statusCode as AutomationStatus),
		[statusCode],
	);

	const errorMessage = useMemo(() => {
		switch (statusCode) {
			case AutomationStatus.ERROR_NOT_ENOUGH_QUOTE:
				return t("automation.errors.EQ1");
			case AutomationStatus.ERROR_PAUSED_EXHAUSTED_QUOTA:
				return t("automation.errors.EQ2");
			case AutomationStatus.ERROR_CONFIGURING_POOL_PROCESSES:
				return t("automation.errors.EP1");
			case AutomationStatus.FAILED_TO_START:
				return t("automation.errors.FTS");
			case AutomationStatus.GENERIC_ERROR:
				return t("automation.errors.ERR");
			default:
				return t("automation.error");
		}
	}, [statusCode, t]);

	const showButton = (statuses: AutomationStatus[]) =>
		statuses.includes(statusCode as AutomationStatus);

	const canPlay = showButton([
		AutomationStatus.PENDING,
		AutomationStatus.ERROR_NOT_ENOUGH_QUOTE,
	]);
	const canPause = showButton([AutomationStatus.STARTED]);
	const canStop = showButton([
		AutomationStatus.STARTED,
		AutomationStatus.PAUSED,
	]);
	const canResume = showButton([
		AutomationStatus.PAUSED,
		AutomationStatus.ERROR_PAUSED_EXHAUSTED_QUOTA,
	]);
	const hasActions = canPlay || canPause || canStop || canResume;

	const executeAction = async (action: () => Promise<void>, label: string) => {
		setProcessingAction(label);
		try {
			await action();
		} finally {
			setProcessingAction(null);
		}
	};

	const handlePlay = () =>
		executeAction(onPlay, t("automation.executing.play"));
	const handlePause = () =>
		executeAction(onPause, t("automation.executing.pause"));
	const handleStop = () =>
		executeAction(onStop, t("automation.executing.stop"));
	const handleResume = () =>
		executeAction(onResume, t("automation.executing.resume"));

	return {
		statusCode,
		typeIcon,
		typeLabelKey,
		statusDotClass,
		isPulsing,
		translatedStatus,
		isError,
		errorMessage,
		canPlay,
		canPause,
		canStop,
		canResume,
		hasActions,
		processingAction,
		handlePlay,
		handlePause,
		handleStop,
		handleResume,
	};
};
